/**
 * 앱 스토어 링크 — 단일 정본.
 *
 * TRAD-3114(후속) — davidyoon 제보: `/about` 상단 CTA가 안드로이드 사용자를 App Store로
 * 보냈다. 원인 조사 중 이 URL이 `AboutPage.tsx`·`SupportPage.tsx` 두 파일에 각자
 * 하드코딩돼 있는 걸 발견했다(`grep -rn "apps.apple.com\|play.google.com" src` — 2곳,
 * 값은 같지만 정의는 갈라져 있었다). **"지금은 값이 같다"가 "안전하다"의 증거가 아니다** —
 * 앱 ID가 바뀌거나 스토어가 하나 더 붙으면 한쪽만 고쳐지는 게 이 팀 최다 사고 유형이다
 * (공용 규격을 일부 표면에만 적용). 여기 하나로 모으고 두 파일이 이걸 쓴다.
 *
 * TRAD-3711 — ~~고정 링크 2개(`APP_LINKS.ios`·`.android`)~~ → «채널 추적 링크» 생성 함수.
 * 정본: docs-ops/marketing/2026-10-12-store-metadata-paste.md §D(규칙)·§G(링크 값, davidyoon 13:4x `pt=123862000`).
 * Jira TRAD-3711 코멘트 14614·14615. 🔴 값은 정본 표에서 그대로 옮긴다(임의 생성 금지) — 테스트가 §G 의 웹 7행을 글자 그대로 고정한다.
 *
 *   iOS : https://apps.apple.com/app/apple-store/id6450615991?pt=123862000&ct=<이름>&mt=8
 *   Play: https://play.google.com/store/apps/details?id=hw.dp.xtrading&utm_source=xtrading_web&utm_medium=web&utm_campaign=<이름>
 *
 * 이름(= iOS ct = Play utm_campaign) 고르는 규칙(§D-3):
 *   첫 진입 utm_campaign 이 허용 목록이면 «출처 이름»(ig_bio→web_ig · x_post→web_x · blog_naver/blog_tistory→web_blog ·
 *   community→web_community), 없거나 허용 밖이면 «버튼 자리 이름»(web_footer · web_about · web_support).
 * 🔴 허용 목록 밖 값은 버린다 — URL 에 아무 문자열이나 넣으면 스토어 링크가 따라가는 것을 막는다(ct 30자·영숫자 제한도 있다).
 */

const IOS_BASE = 'https://apps.apple.com/app/apple-store/id6450615991?pt=123862000';
const PLAY_BASE = 'https://play.google.com/store/apps/details?id=hw.dp.xtrading&utm_source=xtrading_web&utm_medium=web';

/** 스토어 버튼이 놓인 자리 — 출처를 모를 때의 이름. */
// TRAD-3709 — `web_content` = 자동 콘텐츠 페이지(리포트 상세·캘린더 일자별·김프) 설치 버튼 공통 이름(팀장 10/12 — 페이지별로 쪼개면 Apple «5 미만 숨김»).
export type StorePlacement = 'web_footer' | 'web_about' | 'web_support' | 'web_content';

/** 첫 진입 utm_campaign → 스토어 링크 이름(§D-3 표). 이 표의 키가 곧 허용 목록이다. */
export const LANDING_CAMPAIGN_TO_STORE_NAME = {
  ig_bio: 'web_ig',
  x_post: 'web_x',
  blog_naver: 'web_blog',
  blog_tistory: 'web_blog',
  community: 'web_community',
} as const;

export type LandingCampaign = keyof typeof LANDING_CAMPAIGN_TO_STORE_NAME;
export type StoreCampaignName = StorePlacement | (typeof LANDING_CAMPAIGN_TO_STORE_NAME)[LandingCampaign];

export const isLandingCampaign = (value: unknown): value is LandingCampaign =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(LANDING_CAMPAIGN_TO_STORE_NAME, value);

/** 출처가 허용 목록이면 출처 이름, 아니면 버튼 자리 이름. 순수 함수. */
export const resolveStoreCampaignName = (
  placement: StorePlacement,
  landingCampaign: string | null | undefined,
): StoreCampaignName =>
  isLandingCampaign(landingCampaign) ? LANDING_CAMPAIGN_TO_STORE_NAME[landingCampaign] : placement;

export interface StoreLinks {
  ios: string;
  android: string;
}

/** 이름 → iOS·Play 추적 링크(§G 모양 그대로). 순수 함수. */
export const buildStoreLinks = (name: StoreCampaignName): StoreLinks => ({
  ios: `${IOS_BASE}&ct=${name}&mt=8`,
  android: `${PLAY_BASE}&utm_campaign=${name}`,
});
